import { useState, useEffect, useMemo, useCallback } from 'react';
import { getPassiveTestData } from '@/components/sop-intake/constants';
import type { FormValues, Analysis, FullAnalysis, Client, Assessment } from '@/lib/types';
import { useSettings } from '@/components/settings/lib/settings-context';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getClient } from '@/components/clients/services/clients-api';
import {
  createAssessment,
  getClientAssessments,
  updateAssessment,
} from '@/components/sop-intake/services/assessments-api';
import { refreshClientClassificationFromSop } from '@/components/clients/lib/refresh-client-classification';
import { safeToDate } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { exportSopAnalysisToPdf } from '@/lib/pdf-export';
import { getSopPhaseGoals, SopPhaseGoals } from '@/lib/posture-analysis/rule-params';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';
import { invalidateParamsCache } from '@/lib/posture-analysis/rule-params';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';
import logger from '@/lib/logging';
import { postEvaluateSop } from '@/lib/evaluation-api-client';

const initialDemoValues: FormValues = {
  L_CervicalAxialRotation: 25,
  R_CervicalAxialRotation: 35,
  L_CervicalLateralFlexion: 28,
  R_CervicalLateralFlexion: 25,
  L_ShoulderIR: 80,
  R_ShoulderIR: 70,
  L_ShoulderER: 95,
  R_ShoulderER: 85,
  L_ShoulderFlexion: 140,
  R_ShoulderFlexion: 155,
  L_ShoulderAbduction: 55,
  R_ShoulderAbduction: 50,
  L_PelvicRotation: 65,
  R_PelvicRotation: 75,
  L_HipExtension: 5,
  R_HipExtension: -5,
  L_HipAbduction: 40,
  R_HipAbduction: 35,
  L_StraightLegRaise: 85,
  R_StraightLegRaise: 75,
  L_FemoralIR: 35,
  R_FemoralIR: 25,
  L_FemoralER: 50,
  R_FemoralER: 40,
  FootPresentation: '[ L ] > [ R ]',
  L_GroundingPerformance: 'Heel/5th',
  R_GroundingPerformance: 'Toes',
};

const initialEmptyValues: FormValues = Object.keys(initialDemoValues).reduce((acc, key) => {
  acc[key] = '';
  return acc;
}, {} as FormValues);

export function useSopIntake(client: Client | null, setClient: (client: Client | null) => void, initialData?: Assessment | null) {
  const [formValues, setFormValues] = useState<FormValues>(initialData || initialEmptyValues);
  const [lastAnalysis, setLastAnalysis] = useState<FullAnalysis | null>(null);
  const { serverUser } = useAuth();
  const [clientName, setClientName] = useState(client?.name || '');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [phaseGoals, setPhaseGoals] = useState<SopPhaseGoals>({});

  const { settings } = useSettings();
  const { toast } = useToast();
  const navigate = useNavigate();
  const version: RuleDocVersion = settings.useLegacySopAssessment ? 'legacy' : 'current';
  const passiveTestData = useMemo(() => getPassiveTestData(version), [version]);

  useEffect(() => {
    invalidateParamsCache();
  }, [version]);

  useEffect(() => {
    if (initialData) {
      setFormValues(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    let cancelled = false;
    async function loadGoals() {
      const goals = await getSopPhaseGoals('P2', version);
      if (!cancelled) setPhaseGoals(goals || {});
    }
    loadGoals();
    return () => { cancelled = true; };
  }, [version]);

  const handleInputChange = useCallback((id: string, value: string) => {
    setFormValues(prev => ({ ...prev, [id]: value }));
  }, []);

  const [analysis, setAnalysis] = useState<Analysis | null>(null);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    const debounceMs = 200;
    const threshold = settings.analysisThreshold || 10;
    const tid = setTimeout(() => {
      postEvaluateSop(
        {
          formValues,
          phaseGoals,
          analysisThreshold: threshold,
          ruleDocVersion: version
        },
        { signal: ac.signal }
      )
        .then((result) => {
          if (!cancelled) setAnalysis(result);
        })
        .catch((err: unknown) => {
          if (cancelled || (err as Error)?.name === 'AbortError') return;
          logger.error('Failed to evaluate SOP intake', err);
          if (!cancelled) setAnalysis(null);
        });
    }, debounceMs);
    return () => {
      cancelled = true;
      ac.abort();
      clearTimeout(tid);
    };
  }, [formValues, phaseGoals, settings.analysisThreshold, version]);

  const lateralizationCounts = useMemo(() => {
    if (!analysis) return { left: 0, right: 0, intact: 0 };
    return {
      left: analysis.left.length,
      right: analysis.right.length,
      intact: analysis.intact.length
    };
  }, [analysis]);

  const completedTestsCount = useMemo(() => {
    if (!analysis) return 0;
    return analysis.left.length + analysis.right.length + analysis.intact.length;
  }, [analysis]);

  const totalTestsCount = useMemo(() => {
    return Object.keys(passiveTestData).filter(key =>
      passiveTestData[key].type !== 'text' && key !== 'FootPresentation'
    ).length;
  }, [passiveTestData]);

  const overallPattern = useMemo(() => {
    if (!analysis || completedTestsCount === 0) return undefined;

    const { left, right, intact } = lateralizationCounts;
    const total = left + right + intact;

    if (total === 0) return undefined;

    const leftPercent = (left / total) * 100;
    const rightPercent = (right / total) * 100;
    const intactPercent = (intact / total) * 100;

    if (intactPercent >= 50) return 'Balanced';
    if (leftPercent > rightPercent && leftPercent >= 30) return 'RLP';
    if (rightPercent > leftPercent && rightPercent >= 30) return 'LLP';
    return 'Mixed';
  }, [analysis, lateralizationCounts, completedTestsCount]);

  useEffect(() => {
    if (analysis) {
      setLastAnalysis({
        clientName: client?.name || '',
        timestamp: new Date().toISOString(),
        inputs: formValues,
        analysis,
      });
    } else {
      setLastAnalysis(null);
    }
  }, [analysis, client, formValues]);

  const resetForm = () => {
    setFormValues({});
    setLastAnalysis(null);
    setSaveStatus('idle');
  };

  const handleSave = async () => {
    if (!client) {
      toast({
        title: "Cannot Save",
        description: "A client must be selected.",
        variant: "destructive"
      });
      return;
    }

    if (!lastAnalysis) {
      toast({
        title: "Cannot Save",
        description: "Please enter at least one assessment value before saving.",
        variant: "destructive"
      });
      return;
    }

    if (!serverUser) {
      toast({
        title: 'Not Signed In',
        description: 'Please sign in to save assessments.',
        variant: 'destructive'
      });
      return;
    }

    setSaveStatus('saving');
    try {
      const clientData = await getClient(client.id);
      if (!clientData) {
          toast({
              title: "Client Not Found",
              description: "The selected client does not exist in the database.",
              variant: "destructive"
          });
          setClient(null);
          setSaveStatus('idle');
          return;
      }

      const isEditMode = initialData?.id;

      if (isEditMode) {
        const updateData = {
          ...formValues,
          updatedAt: new Date()
        };
        await updateAssessment(client.id, initialData.id, updateData);

        toast({
          title: "Assessment Updated",
          description: `SOP Assessment for ${clientDisplayLabel(client)} has been updated.`,
        });

        void (async () => {
          try {
            const assessments = await getClientAssessments(client.id);
            const latest = [...assessments].sort((a, b) => {
              const dateA = safeToDate((a as any).date)?.getTime() ?? 0;
              const dateB = safeToDate((b as any).date)?.getTime() ?? 0;
              return dateB - dateA;
            })[0];
            if (!latest || latest.id === initialData.id) {
              await refreshClientClassificationFromSop(
                client.id,
                formValues,
                analysis,
                client.classification || ''
              );
            }
          } catch (error) {
            logger.warn('Skipping classification refresh after SOP update', error);
          }
        })();

        setSaveStatus('saved');
        setTimeout(() => {
          setSaveStatus('idle');
          navigate(`/clients/${client.id}/assessment/${initialData.id}`);
        }, 1500);
      } else {
        const newAssessment = {
          date: new Date(),
          type: "SOP",
          ...formValues,
          clientId: client.id
        };

        const docId = await createAssessment(client.id, newAssessment as Omit<Assessment, 'id'>);

        toast({
          title: "Assessment Saved",
          description: `SOP Assessment for ${clientDisplayLabel(client)} has been saved.`,
        });

        setSaveStatus('saved');
        setTimeout(() => {
          setSaveStatus('idle');
          navigate(`/clients/${client.id}/assessment/${docId}`);
        }, 1500);

        void refreshClientClassificationFromSop(
          client.id,
          formValues,
          analysis,
          client.classification || ''
        );
      }

    } catch (error) {
      logger.error("Error saving assessment:", error);
      toast({
        title: "Error Saving",
        description: "Failed to save assessment to the database.",
        variant: "destructive"
      })
      setSaveStatus('idle');
    }
  };

  const handleExportPdf = async () => {
    if (!lastAnalysis) {
      toast({ title: "No Analysis to Export", description: "Please complete the assessment before exporting.", variant: "destructive" });
      return;
    }
     try {
      const assessmentToExport = {
        id: initialData?.id || 'new',
        date: initialData?.date || new Date(),
        ...formValues
      } as Assessment;

      await exportSopAnalysisToPdf(assessmentToExport, client);
    } catch (error) {
       logger.error("Failed to export SOP PDF:", error);
       toast({ title: "Export Failed", description: "Could not generate the PDF for the SOP assessment.", variant: "destructive" });
    }
  };

  return {
    formValues,
    setFormValues,
    handleInputChange,
    analysis,
    clientName,
    setClientName,
    handleSave,
    handleExportPdf,
    resetForm,
    lastAnalysis,
    saveStatus,
    lateralizationCounts,
    completedTestsCount,
    totalTestsCount,
    overallPattern,
    phaseGoals
  };
}
